import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Brain, Wind, Target, Flame, Music, Heart, Eye, Sparkles, Leaf,
  Clock, Calendar as CalendarIcon, Trophy, Zap, TrendingUp, Star, Flame as FireIcon
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  meditation: Brain, breathwork: Wind, chakra: Target, kundalini: Flame,
  mantras: Music, yoga: Heart, consciousness: Eye, ancient_secrets: Sparkles, mindfulness: Leaf,
};

const CATEGORY_NAMES = {
  meditation: "Meditation", breathwork: "Breathwork", chakra: "Chakra",
  kundalini: "Kundalini", mantras: "Mantras", yoga: "Yoga",
  consciousness: "Consciousness", ancient_secrets: "Ancient Secrets", mindfulness: "Mindfulness",
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-6 rounded-2xl bg-white border border-[#E3DDD2]" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent ? "bg-[#D4AF37]/10" : "bg-[#8FA69A]/10"}`}>
        <Icon className={`w-5 h-5 ${accent ? "text-[#D4AF37]" : "text-[#8FA69A]"}`} strokeWidth={1.5} />
      </div>
      <p className="font-heading text-2xl font-medium text-[#1A221E]">{value}</p>
      <p className="text-sm text-[#737E79] mt-1">{label}</p>
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [practiceDates, setPracticeDates] = useState([]);
  const [streaks, setStreaks] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/progress/stats`),
      axios.get(`${API}/favorites`),
      axios.get(`${API}/progress`),
      axios.get(`${API}/streaks`),
    ]).then(([statsRes, favRes, progressRes, streakRes]) => {
      setStats(statsRes.data);
      setFavorites(favRes.data);
      setStreaks(streakRes.data);
      const dates = progressRes.data.map((p) => {
        const d = new Date(p.completed_at);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      });
      setPracticeDates(dates);
      setLoading(false);
    }).catch((err) => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="w-8 h-8 rounded-full border-2 border-[#8FA69A] border-t-transparent animate-spin" />
      </div>
    );
  }

  const totalPractices = stats?.unique_practices || 0;
  const maxPracticesGoal = 20;
  const progressPercent = Math.min((totalPractices / maxPracticesGoal) * 100, 100);

  // Consciousness level based on total sessions
  const getLevel = (sessions) => {
    if (sessions >= 100) return { name: "Cosmic Consciousness", tier: 7 };
    if (sessions >= 50) return { name: "Transcendent Awareness", tier: 6 };
    if (sessions >= 30) return { name: "Illuminated Mind", tier: 5 };
    if (sessions >= 20) return { name: "Awakened Heart", tier: 4 };
    if (sessions >= 10) return { name: "Inner Explorer", tier: 3 };
    if (sessions >= 5) return { name: "Mindful Seeker", tier: 2 };
    if (sessions >= 1) return { name: "Curious Beginner", tier: 1 };
    return { name: "Unawakened", tier: 0 };
  };

  const level = getLevel(stats?.total_sessions || 0);

  return (
    <main className="min-h-screen" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-16">
        {/* Header */}
        <div className="mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2">
            Your Sacred Journey
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light text-[#1A221E]">
            Dashboard
          </h1>
        </div>

        {/* Consciousness Level */}
        <div
          className="relative overflow-hidden rounded-2xl bg-white border border-[#E3DDD2] p-8 mb-8"
          data-testid="consciousness-level"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#737E79]">
                Consciousness Level
              </span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A221E]">
              {level.name}
            </h2>
            <p className="text-sm text-[#737E79] mt-1">
              Tier {level.tier} of 7 — {level.tier < 7 ? `${(level.tier + 1) * 10 - (stats?.total_sessions || 0)} more sessions to next level` : "You have reached the highest level"}
            </p>
            <div className="mt-4 max-w-md">
              <Progress
                value={(level.tier / 7) * 100}
                className="h-3 bg-[#EAE2D6] rounded-full"
              />
            </div>
          </div>
        </div>

        {/* Streak & Reminder */}
        {streaks && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="p-6 rounded-2xl bg-white border border-[#E3DDD2] relative overflow-hidden" data-testid="streak-card">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/5 rounded-full -translate-y-1/2 translate-x-1/3" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#737E79]">Practice Streak</span>
                </div>
                <div className="flex items-end gap-3">
                  <span className="font-heading text-4xl font-medium text-[#1A221E]">{streaks.current_streak}</span>
                  <span className="text-sm text-[#737E79] pb-1">day{streaks.current_streak !== 1 ? "s" : ""}</span>
                </div>
                <p className="text-xs text-[#737E79] mt-2">
                  Longest: {streaks.longest_streak} day{streaks.longest_streak !== 1 ? "s" : ""} | Total: {streaks.total_days} day{streaks.total_days !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
            <div className={`p-6 rounded-2xl border ${streaks.practiced_today ? "bg-[#8FA69A]/5 border-[#8FA69A]/30" : "bg-[#D4AF37]/5 border-[#D4AF37]/30"}`} data-testid="daily-reminder">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#737E79]">Today</span>
              </div>
              {streaks.practiced_today ? (
                <div>
                  <p className="font-heading text-lg font-medium text-[#2C4A3B]">Practice Complete</p>
                  <p className="text-sm text-[#737E79] mt-1">You've honored your practice today. Rest and integrate.</p>
                </div>
              ) : (
                <div>
                  <p className="font-heading text-lg font-medium text-[#8B7520]">Time to Practice</p>
                  <p className="text-sm text-[#737E79] mt-1">Your daily practice awaits. Keep the streak alive!</p>
                  <Link
                    to="/library"
                    data-testid="start-practice-reminder"
                    className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#8FA69A] text-white rounded-full text-xs font-medium hover:bg-[#2C4A3B] transition-all"
                  >
                    Start Practice
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Zap} label="Total Sessions" value={stats?.total_sessions || 0} />
          <StatCard icon={Clock} label="Hours Practiced" value={stats?.total_hours || 0} accent />
          <StatCard icon={Trophy} label="Practices Tried" value={totalPractices} />
          <StatCard icon={TrendingUp} label="Categories" value={stats?.category_stats?.length || 0} accent />
        </div>

        <Tabs defaultValue="progress">
          <TabsList
            data-testid="dashboard-tabs"
            className="h-auto bg-transparent p-0 gap-2 mb-8"
          >
            <TabsTrigger
              value="progress"
              data-testid="tab-progress"
              className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#8FA69A] data-[state=active]:text-white data-[state=active]:shadow-none bg-white border border-[#E3DDD2] text-[#737E79]"
            >
              Progress
            </TabsTrigger>
            <TabsTrigger
              value="calendar"
              data-testid="tab-calendar"
              className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#8FA69A] data-[state=active]:text-white data-[state=active]:shadow-none bg-white border border-[#E3DDD2] text-[#737E79]"
            >
              Calendar
            </TabsTrigger>
            <TabsTrigger
              value="favorites"
              data-testid="tab-favorites"
              className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#8FA69A] data-[state=active]:text-white data-[state=active]:shadow-none bg-white border border-[#E3DDD2] text-[#737E79]"
            >
              Favorites
            </TabsTrigger>
          </TabsList>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Breakdown */}
              <div className="p-6 rounded-2xl bg-white border border-[#E3DDD2]" data-testid="category-breakdown">
                <h3 className="font-heading text-lg font-medium text-[#1A221E] mb-4">Category Breakdown</h3>
                {stats?.category_stats?.length > 0 ? (
                  <div className="space-y-4">
                    {stats.category_stats.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.category] || Sparkles;
                      const catName = CATEGORY_NAMES[cat.category] || cat.category;
                      const maxCount = Math.max(...stats.category_stats.map((c) => c.count));
                      return (
                        <div key={cat.category}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-[#8FA69A]" strokeWidth={1.5} />
                              <span className="text-sm text-[#1A221E]">{catName}</span>
                            </div>
                            <span className="text-xs text-[#737E79]">{cat.count} sessions</span>
                          </div>
                          <Progress
                            value={(cat.count / maxCount) * 100}
                            className="h-2 bg-[#EAE2D6]"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-[#737E79]">No sessions recorded yet. Start a practice!</p>
                )}
              </div>

              {/* Recent Sessions */}
              <div className="p-6 rounded-2xl bg-white border border-[#E3DDD2]" data-testid="recent-sessions">
                <h3 className="font-heading text-lg font-medium text-[#1A221E] mb-4">Recent Sessions</h3>
                {stats?.recent_sessions?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_sessions.map((session, i) => {
                      const Icon = CATEGORY_ICONS[session.category] || Sparkles;
                      const timeAgo = new Date(session.completed_at).toLocaleDateString();
                      return (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-[#E3DDD2] last:border-0">
                          <div className="w-8 h-8 rounded-lg bg-[#EAE2D6] flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-[#8FA69A]" strokeWidth={1.5} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-[#1A221E] truncate">{session.practice_title}</p>
                            <p className="text-xs text-[#737E79]">{timeAgo} · {session.duration_minutes} min</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-[#737E79]">No sessions yet. Begin your journey!</p>
                )}
              </div>

              {/* Practice Goal */}
              <div className="md:col-span-2 p-6 rounded-2xl bg-white border border-[#E3DDD2]" data-testid="practice-goal">
                <h3 className="font-heading text-lg font-medium text-[#1A221E] mb-4">Practice Mastery</h3>
                <p className="text-sm text-[#737E79] mb-3">
                  {totalPractices} of {maxPracticesGoal} unique practices explored
                </p>
                <Progress value={progressPercent} className="h-3 bg-[#EAE2D6] rounded-full" />
                <p className="text-xs text-[#8FA69A] mt-2">
                  {progressPercent >= 100
                    ? "All practices mastered! You are a true seeker."
                    : `${maxPracticesGoal - totalPractices} more practices to unlock all wisdom`}
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="flex flex-col items-center">
              <div className="p-6 rounded-2xl bg-white border border-[#E3DDD2] inline-block" data-testid="practice-calendar">
                <h3 className="font-heading text-lg font-medium text-[#1A221E] mb-4 text-center">
                  Practice Calendar
                </h3>
                <Calendar
                  mode="single"
                  selected={calendarDate}
                  onSelect={(d) => d && setCalendarDate(d)}
                  modifiers={{ practiced: practiceDates }}
                  modifiersStyles={{
                    practiced: {
                      backgroundColor: "#8FA69A",
                      color: "white",
                      borderRadius: "50%",
                    },
                  }}
                  className="rounded-xl"
                />
                <p className="text-xs text-[#737E79] text-center mt-3">
                  <span className="inline-block w-3 h-3 rounded-full bg-[#8FA69A] mr-1 align-middle" />
                  Days you practiced
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {favorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="favorites-grid">
                {favorites.map((practice) => {
                  const Icon = CATEGORY_ICONS[practice.category] || Sparkles;
                  return (
                    <Link
                      key={practice.id}
                      to={`/practice/${practice.id}`}
                      data-testid={`favorite-${practice.id}`}
                      className="group p-6 rounded-2xl bg-white border border-[#E3DDD2] hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                    >
                      <h3 className="font-heading text-lg font-medium text-[#1A221E] group-hover:text-[#2C4A3B] transition-colors">
                        {practice.title}
                      </h3>
                      <p className="mt-2 text-sm text-[#737E79] line-clamp-2">
                        {practice.description}
                      </p>
                      <div className="mt-3 flex items-center gap-2 text-xs text-[#8FA69A]">
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="capitalize">{practice.category.replace("_", " ")}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16" data-testid="no-favorites">
                <Heart className="w-10 h-10 text-[#E3DDD2] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[#737E79]">No favorites yet. Explore the library and save practices you love.</p>
                <Link
                  to="/library"
                  data-testid="browse-library-link"
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2 bg-[#8FA69A] text-white rounded-full text-sm font-medium hover:bg-[#2C4A3B] transition-all duration-300"
                >
                  Browse Library
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
